'use client';

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../../store/auth-store';
import { Icon } from '@/src/components/ui/Icon';
import { Input } from '@workspace/ui/components/input';
import { Label } from '@workspace/ui/components/label';
import { FormWrapper } from '../FormWrapper';
import { Button } from '@workspace/ui/components/button';

const schema = z.object({
  firstName: z.string().min(2, 'First name is required'),
  lastName: z.string().min(2, 'Last name is required'),
});

type FormData = z.infer<typeof schema>;

export const FullNameStep = ({
  onNext,
}: {
  onNext: () => void;
}) => {
  const { data, setData } = useAuthStore();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: data.firstName || '',
      lastName: data.lastName || '',
    },
  });

  const firstName = watch('firstName');
  const lastName = watch('lastName');

  useEffect(() => {
    setData({ firstName, lastName });
  }, [firstName, lastName, setData]);

  const onSubmit = (values: FormData) => {
    setData(values);
    onNext();
  };

  const isFilled = !!firstName && !!lastName;

  return (
    <>
      <FormWrapper title="Enter your full name" personalLink={data.uniqueLink}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full xl:md:px-28 lg:px-10"
        >
          <Label className="w-full relative flex flex-col items-start text-[16px]">
            <Input
              type="text"
              className="border w-full rounded-md placeholder:text-gray-text md:placeholder:text-[16px] md:h-[51px] h-10 font-light md:text-[16px] text-xs"
              {...register('firstName')}
              placeholder="First name"
              aria-invalid={!!errors.firstName}
            />
            {errors.firstName && (
              <p className="text-red-error md:text-sm text-xs font-light flex items-center gap-2.5 ">
                <Icon name="exclamation-mark" className="size-4" />
                {errors.firstName.message}
              </p>
            )}
          </Label>

          <Label className="w-full relative flex flex-col items-start text-[16px] md:mt-3 mt-2">
            <Input
              type="text"
              className="border w-full rounded-md placeholder:text-gray-text md:placeholder:text-[16px] md:h-[51px] h-10 font-light md:text-[16px] text-xs"
              {...register('lastName')}
              placeholder="Last name"
              aria-invalid={!!errors.lastName}
            />
            {errors.lastName && (
              <p className="text-red-error md:text-sm text-xs font-light flex items-center gap-2.5 ">
                <Icon name="exclamation-mark" className="size-4" />
                {errors.lastName.message}
              </p>
            )}
          </Label>

          <Button
            className="w-full md:mt-8 mt-5 md:h-[51px] h-10"
            disabled={!!errors.firstName || !!errors.lastName || !isFilled}
          >
            Next
          </Button>
        </form>
      </FormWrapper>
    </>
  );
};

